import { Flex, Tabs, Typography } from "antd";
import { isEmpty } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UniformTypes } from "~enums/uniformTypes.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { Loader } from "../../Common/Components";
import PermissionCheck from "../../Common/PermissionCheck";
import { useFetchUniforms } from "./hooks/useFetchUniforms";
import type { IUniformModel } from "./models/IUniformModel";
import { Uniform } from "./Uniform";

const Wrapper = styled.div`
  margin-top: 10px;

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    border-radius: 0 !important;
  }

  .ant-tabs-tab-active {
    border-top: 1px solid #FF0000 !important;
  }
`;

const NewTabKey = "new-uniform-tab-key";

export interface IUniformsProperties {
  baseType: UniformTypes;
  baseId: string;
}

export const Uniforms = (properties: IUniformsProperties) => {
  const { formatMessage } = useIntl();
  const [currentTabKey, setCurrentTabKey] = useState<string>();
  const { data: uniformsData, isPending: isLoading } = useFetchUniforms(properties.baseType, properties.baseId);
  const { hasPermission } = useUserPermissions();
  const hasPermissionToCreateUniform = hasPermission(PermissionNames.UNIFORM, PermissionOperations.CREATE);
  const { uniforms } = useSportConfiguration();
  const [showNewTab, setShowNewTab] = useState<boolean>();

  const onAddedUniformHandler = useCallback(async (uniformId: string) => {
    setCurrentTabKey(uniformId);
    setShowNewTab(false);
  }, []);

  const onTabsEditHandler = async (key: any, action: "add" | "remove") => {
    if (action === "add") {
      setShowNewTab(true);
      setCurrentTabKey(NewTabKey);
    } else if (key === NewTabKey) {
      setShowNewTab(false);
      setCurrentTabKey("");
    }
  };

  const tabs = useMemo(() => {
    const array = (uniformsData || []).map((x: IUniformModel) => ({
      label: x.nameLocal,
      key: x.uniformId,
      closable: false,
      children: (
        <Uniform
          key={x.uniformId}
          baseType={properties.baseType}
          baseId={properties.baseId}
          uniform={x}
          onDeleted={() => setCurrentTabKey("")}
        />
      ),
    }));

    if (showNewTab) {
      array.push({
        label: "*",
        key: NewTabKey,
        closable: true,
        children: <Uniform baseType={properties.baseType} baseId={properties.baseId} onAdded={onAddedUniformHandler} />,
      });
    }

    return array;
  }, [properties.baseType, properties.baseId, uniformsData, showNewTab, onAddedUniformHandler]);

  // Current sport does not support uniforms
  if (!uniforms || uniforms.items.length === 0 || uniforms.items.flatMap((x) => x.itemTypes).length === 0) {
    return;
  }
  return (
    <PermissionCheck permissionKeys={[PermissionNames.UNIFORM]} permissionOptions={[PermissionOperations.READ]}>
      <Flex vertical>
        <Typography.Title level={4}>{formatMessage({ id: "uniforms.title" })}</Typography.Title>
        {isLoading && <Loader />}
        {!isLoading && (
          <Wrapper>
            <Tabs
              activeKey={isEmpty(currentTabKey) ? tabs?.[0]?.key : currentTabKey}
              type="editable-card"
              items={tabs}
              onEdit={onTabsEditHandler}
              onChange={(key) => setCurrentTabKey(key)}
              hideAdd={!hasPermissionToCreateUniform || showNewTab}
            />
          </Wrapper>
        )}
      </Flex>
    </PermissionCheck>
  );
};
